import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  signal,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { disabled, form, minDate, required, submit, validate } from '@angular/forms/signals';

import { CustomIcon, IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import {
  ElementActions,
  ElementTypes,
  FinnairThemeService,
  GaContext,
  getCarTrawlerLang,
  ThirdPartyAncillariesProvider,
} from '@fcom/common';
import { GtmService } from '@fcom/common/gtm/services/gtm.service';
import { SelectOption } from '@fcom/common/interfaces/form-interface';
import { ConfigService, WindowRef } from '@fcom/core';
import { LocalDate } from '@fcom/core/utils/tz-date';
import { LanguageService } from '@fcom/ui-translate';
import {
  ButtonMode,
  ButtonSize,
  ButtonTheme,
  DateLabels,
  DateRange,
  IconPosition,
  NotificationLayout,
  RadioItemGroupOption,
  UiLabels,
} from '@fcom/ui-components';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

import { GTM_BOOKING_WIDGET_ELEMENT_IDS, mapToGaState } from '../../../manage-booking/constants';
import type { CarRentalSearchForm } from '../../interfaces/car-rental.interface';
import type { LocationParam } from '../../interfaces/location-pair.interface';
import { carRentalDeepLink, resolveCurrencyCode } from '../../utils/utils.car-rental';

const DRIVER_AGE_PRIMARY = 30;
const DRIVER_AGE_OTHER = 20;
const CALENDAR_DAYS = 360;

const pad = (value: number): string => String(value).padStart(2, '0');

const startOfToday = (): Date => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

@Component({
  selector: 'fin-car-rental-search-widget',
  templateUrl: './car-rental-search-widget.component.html',
  styleUrls: ['./car-rental-search-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CarRentalSearchWidgetComponent {
  configService = inject(ConfigService);
  themeService = inject(FinnairThemeService);
  private languageService = inject(LanguageService);
  private windowRef = inject(WindowRef);
  private gtmService = inject(GtmService);

  readonly ButtonTheme = ButtonTheme;
  readonly ButtonSize = ButtonSize;
  readonly ButtonMode = ButtonMode;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly CustomIcon = CustomIcon;
  readonly IconLibrary = IconLibrary;
  readonly NotificationLayout = NotificationLayout;
  readonly IconPosition = IconPosition;
  readonly timeOptions: SelectOption[] = Array.from({ length: 48 }, (_, index) => {
    const time = `${pad(Math.floor(index / 2))}:${index % 2 ? '30' : '00'}`;
    return { value: time, name: time };
  });
  readonly ageOptions: RadioItemGroupOption[] = [{ value: 'primary', labelText: '30 - 69' }, { value: 'other' }];

  readonly datepickerLabels$ = this.languageService.translate<UiLabels>('datepicker');
  readonly dateLabels$ = this.languageService.translate<DateLabels>('date');

  datesModalOpen = false;
  readonly activeDateField = signal<'pickup' | 'dropoff' | null>(null);

  timeModalOpen = false;
  readonly activeTimeField = signal<'pickup' | 'dropoff' | null>(null);

  private readonly today = startOfToday();
  readonly isDarkTheme = computed(() => this.themeService.siteTheme() === FinnairTheme.DARK_THEME);
  private readonly countryCode = toSignal(this.languageService.countryCode);

  readonly model = signal<CarRentalSearchForm>({
    pickupLocation: null,
    differentDropoff: false,
    dropoffLocation: null,
    pickupDate: null,
    pickupTime: '08:00',
    dropoffDate: null,
    dropoffTime: '08:00',
    driverAge: DRIVER_AGE_PRIMARY,
  });

  readonly form = form(this.model, (path) => {
    required(path.pickupLocation);
    required(path.pickupDate);
    required(path.dropoffDate);
    required(path.pickupTime);
    required(path.dropoffTime);
    required(path.driverAge);
    minDate(path.pickupDate, this.today);
    minDate(path.dropoffDate, this.today);
    disabled(path.dropoffLocation, { when: ({ valueOf }) => !valueOf(path.differentDropoff) });
    required(path.dropoffLocation);
    validate(path.dropoffDate, ({ value, valueOf }) => {
      const dropoff = value();
      const pickup = valueOf(path.pickupDate);
      return dropoff && pickup && dropoff < pickup ? { kind: 'dropoff-before-pickup' } : undefined;
    });
  });

  readonly differentDropoffControl = new UntypedFormControl(false);
  readonly checkboxForm = new UntypedFormGroup({ differentDropoff: this.differentDropoffControl });

  readonly driverAgeControl = new UntypedFormControl('primary');
  readonly ageForm = new UntypedFormGroup({ driverAge: this.driverAgeControl });

  readonly pickupLocalDate = computed<LocalDate | undefined>(() => {
    const date = this.form.pickupDate().value();
    return date ? LocalDate.forDateObj(date) : undefined;
  });

  readonly dropoffLocalDate = computed<LocalDate | undefined>(() => {
    const date = this.form.dropoffDate().value();
    return date ? LocalDate.forDateObj(date) : undefined;
  });

  readonly datepickerSelectedDates = computed<[LocalDate] | undefined>(() => {
    const selected = this.activeDateField() === 'dropoff' ? this.dropoffLocalDate() : this.pickupLocalDate();
    return selected ? [selected] : undefined;
  });

  readonly datepickerRange = computed<DateRange>(() => {
    const max = LocalDate.now().plusDays(CALENDAR_DAYS);
    const min = this.activeDateField() === 'dropoff' ? (this.pickupLocalDate() ?? LocalDate.now()) : LocalDate.now();
    return [min, max];
  });

  readonly activeTimeValue = computed<string | null>(() =>
    this.activeTimeField() === 'dropoff' ? this.form.dropoffTime().value() : this.form.pickupTime().value()
  );

  private readonly timeOptionButtons = viewChildren<ElementRef<HTMLButtonElement>>('timeOption');

  private readonly selectedTimeOptionButton = computed(() => {
    const index = this.timeOptions.findIndex((option) => option.value === this.activeTimeValue());
    return index >= 0 ? this.timeOptionButtons()[index] : undefined;
  });

  constructor() {
    afterRenderEffect(() => {
      const button = this.selectedTimeOptionButton()?.nativeElement;
      if (button) {
        button.scrollIntoView({ block: 'start' });
        button.focus({ preventScroll: true });
      }
    });
    this.differentDropoffControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((differentDropoff) => this.form.differentDropoff().value.set(differentDropoff));
    this.driverAgeControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((age) => this.form.driverAge().value.set(age === 'other' ? DRIVER_AGE_OTHER : DRIVER_AGE_PRIMARY));
  }

  openDateModal(field: 'pickup' | 'dropoff'): void {
    this.activeDateField.set(field);
    this.datesModalOpen = true;
  }

  closeDatesModal(): void {
    this.datesModalOpen = false;
    this.activeDateField.set(null);
  }

  openTimeModal(field: 'pickup' | 'dropoff'): void {
    this.activeTimeField.set(field);
    this.timeModalOpen = true;
  }

  closeTimeModal(): void {
    this.timeModalOpen = false;
    this.activeTimeField.set(null);
  }

  selectTime(time: string): void {
    if (this.activeTimeField() === 'dropoff') {
      this.form.dropoffTime().value.set(time);
    } else {
      this.form.pickupTime().value.set(time);
    }
    this.closeTimeModal();
  }

  onPickupSelected(event: LocationParam): void {
    this.form.pickupLocation().value.set(event.locations.origin ?? null);
  }

  onDropoffSelected(event: LocationParam): void {
    this.form.dropoffLocation().value.set(event.locations.origin ?? null);
  }

  updateSingleDate([dateString]: [string] | [string, string]): void {
    const date = dateString ? new LocalDate(dateString).toDate() : null;

    if (this.activeDateField() === 'dropoff') {
      if (date?.getTime() !== this.form.dropoffDate().value()?.getTime()) {
        this.form.dropoffDate().value.set(date);
      }
    } else if (date?.getTime() !== this.form.pickupDate().value()?.getTime()) {
      this.form.pickupDate().value.set(date);
    }
  }

  async onSearch(): Promise<void> {
    await submit(this.form, async () => {
      this.openDeepLink(this.model());
    });
  }

  private openDeepLink(model: CarRentalSearchForm): void {
    const { apiUrl, clientId } = this.configService.cfg.carRental;
    const locale = getCarTrawlerLang(ThirdPartyAncillariesProvider.CAR_TRAWLER_RENTAL, this.languageService.langValue);
    const currency = resolveCurrencyCode(this.countryCode());
    const url = carRentalDeepLink(model, { apiUrl, clientId, locale, currency });

    this.gtmService.trackElement(
      GTM_BOOKING_WIDGET_ELEMENT_IDS[ThirdPartyAncillariesProvider.CAR_TRAWLER_RENTAL],
      GaContext.THIRD_PARTY_ANCILLARY,
      ElementTypes.LINK,
      mapToGaState[ThirdPartyAncillariesProvider.CAR_TRAWLER_RENTAL],
      ElementActions.CLICK
    );

    this.windowRef.nativeWindow.open(url, '_blank', 'noopener,noreferrer');
  }
}
